import React, { useState, useEffect, useCallback } from 'react';
import { 
  Alert,
  AlertTitle,
  Box, 
  Button, 
  Card, 
  CardContent, 
  Container, 
  Grid, 
  LinearProgress, 
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  CardHeader,
  Divider,
  CircularProgress
} from '@mui/material';
import { ArrowBack, Delete, LocalShipping, Update, Add as AddIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import shipmentService from '../../services/shipmentService';
import { Shipment as BaseShipment, ShipmentStatus } from '../../types/shipment';

// Address interface for formatting
interface Address {
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

// Extend the base Shipment type with UI-specific fields
interface Shipment extends Omit<BaseShipment, 'origin' | 'destination'> {
  origin: string | Address;
  destination: string | Address;
  estimated_delivery?: string;
}

interface ShipmentDetailState {
  shipment: Shipment | null;
  loading: boolean;
  error: string | null;
}

const ShipmentDetail: React.FC = () => {
  const { trackingNumber = '' } = useParams<{ trackingNumber: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [state, setState] = useState<ShipmentDetailState>({
    shipment: null,
    loading: true,
    error: null,
  });
  
  const { shipment: currentShipment, loading, error } = state;

  // Helper function to get tracking number from multiple sources
  const getTrackingNumber = useCallback(() => {
    const result = currentShipment?.tracking_number || 
                   currentShipment?.trackingNumber || 
                   trackingNumber || 
                   null;
    console.log('ShipmentDetail - getTrackingNumber():', {
      fromShipment_snake: currentShipment?.tracking_number,
      fromShipment_camel: currentShipment?.trackingNumber,
      fromUrlParam: trackingNumber,
      result
    });
    return result;
  }, [currentShipment, trackingNumber]);

  const fetchShipment = useCallback(async () => {
    // Validate tracking number before making the API call
    if (!trackingNumber || typeof trackingNumber !== 'string' || trackingNumber.trim() === '') {
      const errorMsg = 'Invalid or missing tracking number';
      console.error(errorMsg);
      setState({
        loading: false,
        error: errorMsg,
        shipment: null
      });
      enqueueSnackbar(errorMsg, { variant: 'error' });
      return;
    }
    
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const shipment = await shipmentService.getShipment(trackingNumber.trim());
      
      if (!shipment) {
        throw new Error('No shipment data received');
      }
      
      setState({
        shipment,
        loading: false,
        error: null
      });
    } catch (err: unknown) {
      console.error('Error fetching shipment:', err);
      
      // Type guard to check if error is an Axios error
      interface AxiosError {
        isAxiosError: boolean;
        response?: {
          status: number;
          data?: {
            message?: string;
          };
        };
        message: string;
      }
      
      const isAxiosError = (error: any): error is AxiosError => {
        return error && typeof error === 'object' && 'isAxiosError' in error;
      };
      
      let errorMsg = 'Failed to load shipment data';
      let statusCode: number | undefined;
      
      if (isAxiosError(err)) {
        errorMsg = err.response?.data?.message || err.message || errorMsg;
        statusCode = err.response?.status;
      } else if (err instanceof Error) {
        errorMsg = err.message;
      }
      
      setState({ 
        loading: false, 
        error: errorMsg,
        shipment: null
      });
      
      enqueueSnackbar(errorMsg, { 
        variant: 'error',
        autoHideDuration: 5000
      });
      
      // Navigate back if the shipment is not found
      if (statusCode === 404) {
        setTimeout(() => navigate('/shipments'), 2000);
      }
    }
  }, [trackingNumber, enqueueSnackbar, navigate]);

  // Initial data fetch
  useEffect(() => {
    fetchShipment();
  }, [fetchShipment]);

  // Handle case when tracking number is missing or invalid
  if (!trackingNumber || !trackingNumber.trim()) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          <AlertTitle>{t('shipments.errors.invalidTrackingNumber')}</AlertTitle>
          {t('shipments.errors.trackingNumberMissing')}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/shipments')}
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Back to Shipments
        </Button>
      </Container>
    );
  }

  // Handle loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/shipments')}
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Back to Shipments
        </Button>
      </Container>
    );
  }
  
  // Handle case when shipment is not found
  if (!currentShipment) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          <AlertTitle>Not Found</AlertTitle>
          Shipment not found
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/shipments')}
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Back to Shipments
        </Button>
      </Container>
    );
  }

  // Format address from address object
  const formatAddress = (address: string | Address): string => {
    if (typeof address === 'string') {
      return address; // Return as is if it's already a string
    }
    
    return [
      address.address_line1,
      address.address_line2,
      `${address.city}, ${address.state} ${address.postal_code}`,
      address.country
    ].filter(Boolean).join('\n');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/shipments')}
        sx={{ mb: 3 }}
      >
        {t('common.backToList')}
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h1">
                  Shipment Details
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" color="textSecondary">Tracking Number</Typography>
                  <Typography variant="body1" gutterBottom>
                    {getTrackingNumber() || 'N/A'}
                  </Typography>
                  
                  <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 2 }}>Status</Typography>
                  <Typography variant="body1" gutterBottom sx={{ textTransform: 'capitalize' }}>
                    {currentShipment.status.replace(/_/g, ' ')}
                  </Typography>
                  
                  <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 2 }}>Origin</Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatAddress(currentShipment.origin)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" color="textSecondary">Destination</Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatAddress(currentShipment.destination)}
                  </Typography>
                  
                  {currentShipment.estimated_delivery && (
                    <>
                      <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 2 }}>
                        Estimated Delivery
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {new Date(currentShipment.estimated_delivery).toLocaleDateString()}
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.actions')}
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Update />}
                  onClick={() => {
                    const trackingNum = getTrackingNumber();
                    if (trackingNum) {
                      navigate(`/shipments/edit/${trackingNum}`);
                    } else {
                      enqueueSnackbar('Unable to get tracking number', { variant: 'error' });
                    }
                  }}
                  fullWidth
                >
                  {t('shipments.actions.updateStatus')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LocalShipping />}
                  onClick={() => {
                    const trackingNum = getTrackingNumber();
                    if (trackingNum) {
                      navigate(`/shipments/edit/${trackingNum}`);
                    } else {
                      enqueueSnackbar('Unable to get tracking number', { variant: 'error' });
                    }
                  }}
                  fullWidth
                >
                  {t('shipments.actions.assignDriver')}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const trackingNum = getTrackingNumber();
                    if (trackingNum) {
                      navigate(`/shipments/edit/${trackingNum}`);
                    } else {
                      enqueueSnackbar('Unable to get tracking number', { variant: 'error' });
                    }
                  }}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {t('shipments.actions.addNote')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  fullWidth
                  onClick={async () => {
                    const trackingNum = getTrackingNumber();
                    if (!trackingNum) {
                      enqueueSnackbar('Unable to get tracking number for deletion', { variant: 'error' });
                      return;
                    }
                    if (window.confirm(t('shipments.messages.confirmDelete'))) {
                      try {
                        await shipmentService.deleteShipment(trackingNum);
                        enqueueSnackbar(t('shipments.messages.deleted'), { variant: 'success' });
                        navigate('/shipments');
                      } catch (error: any) {
                        console.error('Error deleting shipment:', error);
                        const errorMessage = error.response?.data?.message || error.message || t('shipments.messages.deleteError');
                        enqueueSnackbar(errorMessage, { variant: 'error' });
                      }
                    }
                  }}
                >
                  {t('common.delete')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ShipmentDetail;
